import { calculatePrice } from "../Utils/validationUtil";
import { IOfferPrice } from "../../Orchestrator/Types/Interface/IOffer";
import type { FareProductRef } from "./fareParams";

export interface BookingAnalyticsParams {
  bookingReference: string;
  price: number;
  currency: string;
  origin: string;
  destination: string;
  travelDate: string; // YYYY-MM-DD
  travelTime: string; // HH:mm
  trainNumber: string;
  trainClass: string;
  ticketOption: string;
  /** Booked fare product, for train_class / fare_type. */
  fareProduct: FareProductRef;
  passengerCount: number;
  /** Comma-separated passenger types, same vocabulary as search_btn. */
  passengerType: string;
  /** IN_BOUND trip of a round trip; "" when one-way. */
  returnDate: string;
  returnTime: string;
}

// OSDM appliedPassengerTypes[].type -> the search-side keys
// (getSearchSelectedPassenger) so every event uses one vocabulary.
const PASSENGER_TYPE_KEYS: Record<string, string> = {
  ADULT: "adults",
  CHILD: "children",
  INFANT: "infants",
  YOUNG_CHILD: "infants",
  SENIOR: "seniorcitizens",
  STUDENT: "students",
};

const toPassengerTypes = (bookedOffers: any[]): string => {
  const types = bookedOffers
    .flatMap((o) => (Array.isArray(o?.admissions) ? o.admissions : []))
    .flatMap((a: any) => (Array.isArray(a?.appliedPassengerTypes) ? a.appliedPassengerTypes : []))
    .map((t: any) => String(t?.type ?? "").toUpperCase())
    .filter(Boolean)
    .map((t: string) => PASSENGER_TYPE_KEYS[t] ?? t.toLowerCase());
  return Array.from(new Set(types)).join(",");
}

// Coerce a date-ish value (ISO string, Date, or dayjs) to an ISO string.
// The booking response carries plain ISO strings, but earlier funnel stages
// can hold Date/dayjs objects — guard so toDate/toTime never throw.
const toIsoString = (v: unknown): string => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  const maybe = v as { toISOString?: () => string };
  if (typeof maybe.toISOString === "function") {
    try {
      return maybe.toISOString();
    } catch {
      return "";
    }
  }
  return String(v);
};

const toDate = (iso?: unknown): string => toIsoString(iso).slice(0, 10);
const toTime = (iso?: unknown): string => {
  const m = toIsoString(iso).match(/T(\d{2}:\d{2})/);
  return m ? m[1] : "";
};

/**
 * Resolve the user-visible price from an OSDM Price object.
 * Reuses the app's calculatePrice (src/Lib/Utils/validationUtil.ts) so our
 * analytics price always equals what the booking UI displays — OSDM `amount`
 * is minor-units scaled, and calculatePrice divides scaled amounts by 100.
 * Guarded so analytics never emits NaN/Infinity on partial data.
 */
const toDisplayPrice = (priceObject?: IOfferPrice): number => {
  if (!priceObject) return 0;
  const value = calculatePrice(priceObject);
  return Number.isFinite(value) ? value : 0;
};

export interface ExtractBookingParamsOptions {
  /**
   * Describe the trip with this direction instead of the first one. Book-return
   * adds the return to the SAME booking, so trips[0] is the original outbound.
   * Falls back to trips[0] when no trip matches.
   */
  tripDirection?: "OUT_BOUND" | "IN_BOUND";
}

/**
 * Map an IBookingResponse to the trip params shared by events #6/#7/#9.
 * Defensive: any missing field falls back to "" / 0 / "AED" so GA4 never
 * receives nulls and the call never throws on partial data.
 */
export function extractBookingParams(
  resp: unknown,
  options: ExtractBookingParamsOptions = {},
): BookingAnalyticsParams {
  const booking = (resp as { booking?: Record<string, unknown> })?.booking ?? {};
  const b = booking as Record<string, any>;
  const trips: any[] = Array.isArray(b?.trips) ? b.trips : [];
  const trip =
    (options.tripDirection && trips.find((t) => t?.direction === options.tripDirection)) ||
    trips[0];
  const leg = trip?.legs?.[0]?.timedLeg;
  const service = leg?.service ?? {};
  const departure = leg?.start?.serviceDeparture?.timetabledTime;
  // Return leg of a round trip — only when the trip described is not it.
  const returnTrip = trips.find((t) => t?.direction === "IN_BOUND" && t !== trip);
  const returnDeparture = returnTrip?.legs?.[0]?.timedLeg?.start?.serviceDeparture?.timetabledTime;
  const bookedOffers: any[] = Array.isArray(b?.bookedOffers) ? b.bookedOffers : [];
  // The fare product carries the real service class + fare name; the leg-level
  // service.productCategory is an unreliable placeholder (e.g. "Imported").
  const product = b?.bookedOffers?.[0]?.products?.[0] ?? {};

  return {
    bookingReference: b?.bookingCode ?? "",
    price: toDisplayPrice(b?.confirmedPrice),
    currency: b?.confirmedPrice?.currency ?? "AED",
    // Station codes (e.g. PDW/PMB) to match the search/offer events (#4/#5).
    // OSDM nests the code: start.stopPlaceRef = { stopPlaceRef: "PDW", ... }.
    // Fall back to the display name only if the code is absent.
    origin: leg?.start?.stopPlaceRef?.stopPlaceRef ?? leg?.start?.stopPlaceName ?? "",
    destination: leg?.end?.stopPlaceRef?.stopPlaceRef ?? leg?.end?.stopPlaceName ?? "",
    travelDate: toDate(departure),
    travelTime: toTime(departure),
    trainNumber: service?.vehicleNumbers?.[0] ?? service?.publishedServiceName ?? "",
    // Real service class from the booked fare product (e.g. "Standard"),
    // falling back to the leg category placeholder only if absent.
    trainClass: product?.serviceClass?.name ?? service?.productCategory?.name ?? "",
    // The fare/ticket product name (e.g. "Standard Saver"), with code as fallback.
    ticketOption: product?.summary ?? product?.description ?? product?.code ?? "",
    fareProduct: {
      code: product?.code ?? "",
      name: product?.summary ?? product?.description ?? "",
      serviceClassType: product?.serviceClass?.type ?? "",
      productTags: Array.isArray(product?.productTags) ? product.productTags : [],
    },
    passengerCount: Array.isArray(b?.passengers) ? b.passengers.length : 0,
    passengerType: toPassengerTypes(bookedOffers),
    returnDate: toDate(returnDeparture),
    returnTime: toTime(returnDeparture),
  };
}
